<template>
    <div id="reservation-app">
        <form id="reservation-form" action="#" onsubmit="return false;">
            <div class="flex-row margin-bottom-15" id="reservation-form__select-room">
                <label class="flex-col">室名絞り込み</label>
                <div class="flex-col">
                    <label class="font-weight-normal">
                        <input type="checkbox" name="reservation-form__select-all"
                               id="reservation-form__select-all"
                               @change="onChangeSelectAllRoom($event)"/>
                        全て選択
                    </label>
                    <fieldset>
                        <div class="flex-row">
                            <div class="flex-col" v-for="roomSet in rooms">
                                <label v-for="room in roomSet" :key="room.code">
                                    <input type="checkbox" name="room[]"
                                           :id="'room_' + room.code"
                                           :value="room.code"
                                           v-model="selectedRooms"/>
                                    {{ room.room }}<span v-if="room.size">（{{ room.size }}）</span></label>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </div>
            <div class="scroll-x">
                <div class="flex-row flex-nowrap margin-bottom-15" id="reservation-form__select-date">
                    <label class="flex-col" for="select-date">表示開始日</label>
                    <div class="flex-col">
                        <input type="text" name="select-date" id="select-date" class="datepicker"
                               :value="displayDate"/>
                        <button id="btn-change-date" @click.prevent="changeDisplayDate">変更する</button>
                    </div>
                    <div class="flex-col">
                        <div class="date-nav">
                            <a href="#" @click.prevent="prevWeek">&laquo; 前の週</a> |
                            <a href="#" @click.prevent="prevDay">&lt; 前の日</a> |
                            <a href="#" @click.prevent="today">今日</a> |
                            <a href="#" @click.prevent="nextDay">次の日 &gt;</a> |
                            <a href="#" @click.prevent="nextWeek">次の週 &raquo;</a>
                        </div>
                    </div>
                    <div class="flex-col text-right" v-if="!loading && lastModified">
                        更新日：<span id="lastModified">{{ lastModifiedString }}</span>
                    </div>
                </div>
                <div class="reservation-loading" v-if="loading">
                    <p>データを読み込んでいます…</p>
                </div>
                <div class="reservation-error" v-if="errorMessage">
                    <p>{{ errorMessage }}</p>
                </div>
                <div class="reservation-error margin-bottom-15" v-if="!loading && !displayRecords.length">
                    <p>該当データがありません。</p>
                </div>
                <table id="reservation-table" class="margin-bottom-15" v-if="!loading && displayRecords.length">
                    <caption>施設予約状況</caption>
                    <thead>
                    <tr>
                        <th class="reservation-table__room-name">室名</th>
                        <th class="reservation-table__time">時間帯</th>
                        <th class="reservation-nav" @click="prevWeek" title="前の週へ"><i
                                class="reservation-nav-prev"></i></th>
                        <th class="reservation-table__date" v-for="date in displayDays"><span class="date">{{ date.format('MM/DD') }}</span><span
                                class="weekday">（{{ date.format('dd') }}）</span></th>
                        <th class="reservation-nav" @click="nextWeek" title="次の週へ"><i
                                class="reservation-nav-next"></i></th>
                    </tr>
                    </thead>
                    <tbody v-for="displayRecord in displayRecords">
                    <tr>
                        <td class="reservation-table__room-name" rowspan="3">{{ displayRecord.room }}</td>
                        <td class="reservation-table__time">午前</td>
                        <td class="reservation-nav" rowspan="3" @click="prevWeek" title="前の週へ"><i
                                class="reservation-nav-prev"></i></td>
                        <td class="reservation-table__data" v-for="status in displayRecord.morning">{{ status === '×' ?
                            '済' : status }}
                        </td>
                        <td class="reservation-nav" rowspan="3" @click="nextWeek" title="次の週へ"><i
                                class="reservation-nav-next"></i></td>
                    </tr>
                    <tr>
                        <td class="reservation-table__time">午後</td>
                        <td class="reservation-table__data" v-for="status in displayRecord.afternoon">{{ status === '×'
                            ? '済' : status }}
                        </td>
                    </tr>
                    <tr>
                        <td class="reservation-table__time">夜間</td>
                        <td class="reservation-table__data" v-for="status in displayRecord.night">{{ status === '×' ?
                            '済' : status }}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <slot name="footer"></slot>
        </form>
    </div>
</template>

<script>
    import jQuery from 'jquery';
    import Moment from 'moment';
    import CsvParser from './lib/ReservationRecord/CsvParser';
    import filterDays from './lib/filterDays';
    import filterRoom from './lib/filterRoom';
    import restructureRecords from './lib/restructureRecords';

    Moment.locale('ja');

    const displayDateFormat = 'YYYY/MM/DD';

    export default {
        data() {
            return {
                loading: true,
                displayDate: Moment().format(displayDateFormat),
                lastModified: null,
                records: [],
                rooms: [],
                errorMessage: false,
                selectedRooms: [],
            };
        },
        computed: {
            /**
             * 最終更新日の文字列
             *
             * @return {string}
             */
            lastModifiedString() {
                return this.lastModified.format('YYYY年MM月DD日（dd）HH:mm');
            },
            /**
             * 日付の表示期間(7日間)
             *
             * @return {Array}
             */
            displayDays() {
                const days = [];
                const base = new Moment(this.displayDate, displayDateFormat);
                for (let i = 0; i < 7; i++) {
                    days.push(base.clone().add(i, 'days'));
                }

                return days;
            },
            /**
             * 表示対象データ
             *
             * @return {Array}
             */
            displayRecords() {
                return restructureRecords(filterRoom(filterDays(this.records, this.displayDays), this.selectedRooms), this.displayDays);
            },
        },
        // For debug
        // watch: {
        //     selectedRooms(newValue) {
        //         console.log('selectedRooms', newValue);
        //     },
        //     displayDate(newValue) {
        //         console.log('displayDate', newValue);
        //     }
        // },
        created() {
            const vm = this;

            // CSVデータの取得
            const dataRequest = jQuery.get('/library/data/csv/reservation.csv');
            dataRequest
                .then((response) => {
                    vm.records = CsvParser(response);
                    const lastModified = dataRequest.getResponseHeader('Last-Modified');
                    if (lastModified) {
                        vm.lastModified = Moment(new Date(lastModified));
                    }
                })
                .fail(() => {
                    vm.errorMessage = '予約データの読み込みに失敗しました。';
                });
            // 部屋データの取得
            const roomRequest = jQuery.get('/library/reservation2019/rooms.json');
            roomRequest
                .then((response) => {
                    vm.rooms = response;
                })
                .fail(() => {
                    vm.errorMessage = '部屋データの読み込みに失敗しました。';
                });

            jQuery.when(dataRequest, roomRequest).done(() => {
                vm.loading = false;
            });
        },
        methods: {
            /**
             * 施設絞り込み 全て選択チェックボックスの処理
             *
             * @param event
             */
            onChangeSelectAllRoom(event) {
                const vm = this;

                if (event.target.checked) {
                    // 施設絞り込みを全てチェックする
                    jQuery('#reservation-form__select-room input[type=checkbox]').each((idx, element) => {
                        if (element.name === 'room[]') {
                            vm.selectedRooms.push(element.value);
                        }
                    });
                } else {
                    this.selectedRooms = [];
                }
            },
            /**
             * 表示開始日の更新
             */
            changeDisplayDate(event) {
                event.preventDefault();
                this.displayDate = jQuery('#select-date').val();
            },
            /**
             * 次の週
             */
            nextWeek(event) {
                event.preventDefault();
                this.displayDate = (new Moment(this.displayDate, displayDateFormat)).add(7, 'days').format(displayDateFormat);
            },
            /**
             * 前の週
             */
            prevWeek(event) {
                event.preventDefault();
                this.displayDate = (new Moment(this.displayDate, displayDateFormat)).subtract(7, 'days').format(displayDateFormat);
            },
            /**
             * 次の日
             */
            nextDay(event) {
                event.preventDefault();
                this.displayDate = (new Moment(this.displayDate, displayDateFormat)).add(1, 'days').format(displayDateFormat);
            },
            /**
             * 前の日
             */
            prevDay(event) {
                event.preventDefault();
                this.displayDate = (new Moment(this.displayDate, displayDateFormat)).subtract(1, 'days').format(displayDateFormat);
            },
            /**
             * 今日
             */
            today(event) {
                event.preventDefault();
                this.displayDate = (new Moment()).format(displayDateFormat);
            },
        }
    }
</script>

<style>
</style>
